/**
 * SmartTokenParamsPanel — right-rail editor for a selected `smart-token`
 * ContentBlock. Mirrors the table-props panel pattern: each param in the
 * SmartBlock's `paramSchema` becomes a labeled control that writes back
 * to `block.smartParams` and flips `setDirty(true)`.
 *
 * Lives next to the table-props block in BlockEditor's right rail.
 */

import React from 'react';
import * as LucideIcons from 'lucide-react';
import type { ContentBlock, SmartBlock, SmartBlockParam } from '../../../shared/types';
import { findSmartBlockInCatalog, builtInIdFor } from '../../../engine/smart-blocks';

interface Props {
  block: ContentBlock;
  catalog: SmartBlock[];
  /** Used by spec-ref-list type to render a checkbox list of other specs */
  otherSpecsInProject?: Array<{ id: string; csiNumber?: string; title?: string }>;
  /** Callback when params change. */
  onChange: (newParams: Record<string, any>) => void;
  /** Open the Smart Block Creator to edit the underlying definition. */
  onOpenCreator?: () => void;
}

const labelStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: 0.6,
  textTransform: 'uppercase',
  color: 'var(--tm)',
  marginBottom: 4,
  display: 'block',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '6px 9px',
  fontSize: 12,
  border: '1px solid var(--bd)',
  borderRadius: 5,
  background: 'var(--sf)',
  color: 'var(--tx)',
  outline: 'none',
  boxSizing: 'border-box',
};

export function SmartTokenParamsPanel({
  block,
  catalog,
  otherSpecsInProject = [],
  onChange,
  onOpenCreator,
}: Props) {
  const sb =
    findSmartBlockInCatalog(block.smartBlockId, catalog) ??
    (block.smartBlockKind
      ? findSmartBlockInCatalog(builtInIdFor(block.smartBlockKind as any), catalog)
      : undefined);
  const params: Record<string, any> = {
    ...(sb?.defaultParams ?? {}),
    ...(block.smartParams ?? {}),
  };
  // @ts-ignore lucide dynamic
  const Icon = (LucideIcons[sb?.icon ?? 'Sparkles'] as any) ?? LucideIcons.Sparkles;
  const accent = sb?.color ?? '#228be6';
  const schema = sb?.paramSchema ?? [];

  function set(key: string, value: any) {
    const next = { ...(block.smartParams ?? {}), [key]: value };
    onChange(next);
  }

  if (!sb) {
    return (
      <div style={{ padding: 12, fontSize: 12, color: 'var(--tm)' }}>
        Smart block definition not found.
      </div>
    );
  }

  return (
    <div style={{ padding: '14px 14px 24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: 7,
            background: `${accent}22`,
            color: accent,
            flexShrink: 0,
          }}
        >
          <Icon size={15} strokeWidth={2.5} />
        </span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--tx)' }}>{sb.name}</div>
          <div style={{ fontSize: 10, color: 'var(--tm)', marginTop: 1 }}>
            {sb.kind === 'custom' ? 'Custom' : 'Built-in'} smart block
          </div>
        </div>
      </div>

      {sb.description && (
        <p style={{ fontSize: 11, lineHeight: 1.5, color: 'var(--tm)', margin: '0 0 14px' }}>
          {sb.description}
        </p>
      )}

      <div
        style={{
          borderTop: '1px solid var(--bd)',
          paddingTop: 12,
        }}
      >
        {schema.length === 0 && (
          <div style={{ fontSize: 11, color: 'var(--tm)', fontStyle: 'italic' }}>
            No configurable parameters.
          </div>
        )}
        {schema.map((p) => (
          <div key={p.key} style={{ marginBottom: 12 }}>
            <label style={labelStyle}>{p.label}</label>
            {renderParamControl(p, params[p.key], (v) => set(p.key, v), {
              otherSpecsInProject,
            })}
            {p.description && (
              <div
                style={{
                  fontSize: 10,
                  color: 'var(--tm)',
                  marginTop: 3,
                  lineHeight: 1.4,
                }}
              >
                {p.description}
              </div>
            )}
          </div>
        ))}
      </div>

      {onOpenCreator && (
        <button
          onClick={onOpenCreator}
          style={{
            marginTop: 14,
            width: '100%',
            padding: '8px 10px',
            fontSize: 11,
            fontWeight: 600,
            border: `1px solid ${accent}66`,
            borderRadius: 6,
            background: `${accent}10`,
            color: accent,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
          }}
        >
          {sb.kind === 'custom' ? 'Open in Smart Block Creator' : 'Manage Smart Blocks Library'}
        </button>
      )}
      {onOpenCreator && sb.kind !== 'custom' && (
        <div
          style={{
            marginTop: 8,
            fontSize: 10,
            color: 'var(--tm)',
            lineHeight: 1.4,
            textAlign: 'center',
          }}
        >
          Built-in definitions are read-only — manage custom smart blocks in the library.
        </div>
      )}
    </div>
  );
}

function renderParamControl(
  p: SmartBlockParam,
  value: any,
  onChange: (v: any) => void,
  ctx: { otherSpecsInProject: Array<{ id: string; csiNumber?: string; title?: string }> }
): React.ReactNode {
  switch (p.type) {
    case 'string':
    case 'division':
      return (
        <input
          type="text"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={p.placeholder}
          style={inputStyle}
        />
      );
    case 'number':
      return (
        <input
          type="number"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
          placeholder={p.placeholder}
          style={inputStyle}
        />
      );
    case 'boolean':
      return (
        <label
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 12,
            color: 'var(--tx)',
            cursor: 'pointer',
          }}
        >
          <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} />
          <span>{value ? 'On' : 'Off'}</span>
        </label>
      );
    case 'enum':
      return (
        <select
          value={value ?? p.default ?? ''}
          onChange={(e) => onChange(e.target.value)}
          style={inputStyle}
        >
          {(p.options ?? []).map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    case 'multiselect': {
      const arr: string[] = Array.isArray(value) ? value : (p.default ?? []);
      return (
        <div
          style={{
            border: '1px solid var(--bd)',
            borderRadius: 5,
            padding: 6,
            background: 'var(--sf)',
            maxHeight: 140,
            overflowY: 'auto',
          }}
        >
          {(p.options ?? []).map((opt) => {
            const checked = arr.includes(opt.value);
            return (
              <label
                key={opt.value}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '2px 0',
                  fontSize: 11,
                  cursor: 'pointer',
                  color: 'var(--tx)',
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => {
                    const next = checked ? arr.filter((v) => v !== opt.value) : [...arr, opt.value];
                    onChange(next);
                  }}
                />
                <span>{opt.label}</span>
              </label>
            );
          })}
        </div>
      );
    }
    case 'spec-ref-list': {
      const arr: string[] = Array.isArray(value) ? value : [];
      if (ctx.otherSpecsInProject.length === 0) {
        return (
          <div
            style={{
              fontSize: 11,
              color: 'var(--tm)',
              fontStyle: 'italic',
              padding: 4,
            }}
          >
            Available at project-compile time.
          </div>
        );
      }
      return (
        <div
          style={{
            border: '1px solid var(--bd)',
            borderRadius: 5,
            padding: 6,
            background: 'var(--sf)',
            maxHeight: 160,
            overflowY: 'auto',
          }}
        >
          {ctx.otherSpecsInProject.map((s) => {
            const checked = arr.includes(s.id);
            return (
              <label
                key={s.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '2px 0',
                  fontSize: 11,
                  cursor: 'pointer',
                  color: 'var(--tx)',
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => {
                    const next = checked ? arr.filter((v) => v !== s.id) : [...arr, s.id];
                    onChange(next);
                  }}
                />
                <span>
                  {s.csiNumber ? `${s.csiNumber} — ` : ''}
                  {s.title ?? '(untitled)'}
                </span>
              </label>
            );
          })}
        </div>
      );
    }
    default:
      return (
        <input
          type="text"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          style={inputStyle}
        />
      );
  }
}
